const uomUnitName = (uom) =>
  String(uom?.unit ?? uom?.value ?? uom?.name ?? "")
    .trim()
    .toLowerCase();

const positiveConversion = (uom) => {
  const value = Number(uom?.conversion);
  return Number.isFinite(value) && value > 0 ? value : 1;
};

export const findMappedUom = (
  uomList,
  { uomMappingId, names = [], clinicId } = {},
) => {
  const list = Array.isArray(uomList) ? uomList : [];
  if (uomMappingId != null && uomMappingId !== "") {
    const byId = list.find(
      (ul) => String(ul?.uomMappingId ?? "") === String(uomMappingId),
    );
    if (byId) return byId;
  }
  for (const name of names) {
    const unit = String(name ?? "")
      .trim()
      .toLowerCase();
    if (!unit) continue;
    const matches = list.filter((ul) => uomUnitName(ul) === unit);
    if (!matches.length) continue;
    const clinicMatch = matches.find(
      (ul) =>
        clinicId != null && String(ul?.createdBy ?? "") === String(clinicId),
    );
    return clinicMatch ?? matches[0];
  }
  return null;
};

// stockBatch.quantity is stored in qtyMapName.
export const findBatchQuantityUom = (uomMappings, batch, clinicId) =>
  findMappedUom(uomMappings, {
    uomMappingId: batch?.qtyMapId,
    names: [batch?.qtyMapName],
    clinicId,
  });

// stockBatch.unitSellingPrice is stored in uomMapName (pricing UOM).
export const findBatchPriceUom = (uomMappings, batch, clinicId) =>
  findMappedUom(uomMappings, {
    uomMappingId: batch?.mapId,
    names: [batch?.uomMapName],
    clinicId,
  });

// Each mapping conversion is how many of that unit equal 1 purchase unit.
// Strip conversion 10 and box conversion 1 means 1 box = 10 strips.
// A strip price is multiplied by 10 for a box. A box price is divided by 10 for a strip.
const batchUnitsPerSelected = (batchUom, selectedUom) => {
  const sameId =
    batchUom?.uomMappingId != null &&
    selectedUom?.uomMappingId != null &&
    String(batchUom.uomMappingId) === String(selectedUom.uomMappingId);
  const batchName = uomUnitName(batchUom);
  const selectedName = uomUnitName(selectedUom);
  if (sameId || (batchName && batchName === selectedName)) return 1;

  return positiveConversion(batchUom) / positiveConversion(selectedUom);
};

export const convertBatchQty = (quantity, batchUom, selectedUom) => {
  const perSelected = batchUnitsPerSelected(batchUom, selectedUom) || 1;
  return Number(quantity ?? 0) / perSelected;
};

export const convertUnitPrice = (unitPrice, batchUom, selectedUom) =>
  Number(unitPrice ?? 0) * (batchUnitsPerSelected(batchUom, selectedUom) || 1);

export const getBatchDisplayInUom = ({
  batch,
  uomMappings,
  selectedUom,
  clinicId,
}) => {
  const batchUom = findBatchQuantityUom(uomMappings, batch, clinicId);
  const targetUom = findMappedUom(uomMappings, {
    uomMappingId: selectedUom?.uomMappingId,
    names: [selectedUom?.name, selectedUom?.value, selectedUom?.unit],
    clinicId,
  });
  if (!targetUom) {
    return {
      uom: batch?.qtyMapName ?? "",
      uomQuantity: batch?.quantity,
    };
  }
  return {
    uom:
      selectedUom?.name ??
      selectedUom?.value ??
      targetUom?.unit ??
      batch?.qtyMapName ??
      "",
    uomQuantity: convertBatchQty(batch?.quantity, batchUom, targetUom),
  };
};
